import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { Plus, Trash2, Pencil, Upload, Download } from "lucide-react";
import { toast } from "sonner";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function PriceListsPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [pl, pr] = await Promise.all([api.get("/price-lists"), api.get("/products")]);
    setRows(pl.data);
    setProducts(pr.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (form, id) => {
    try {
      if (id) await api.put(`/price-lists/${id}`, form);
      else await api.post("/price-lists", form);
      toast.success("Saved");
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this price list?")) return;
    try {
      await api.delete(`/price-lists/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/price-lists/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "price_lists_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("template download failed:", err);
      toast.error("Download failed");
    }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await api.post("/price-lists/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created = 0, updated = 0, skipped_missing_product = 0, errors = [] } = res.data || {};
      toast.success(`Uploaded — ${created} created, ${updated} updated${skipped_missing_product ? `, ${skipped_missing_product} rows skipped` : ""}`);
      if (errors.length) logger.warn("Upload had row errors:", errors);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <PageHeader
        title="Price Lists"
        subtitle="Manage rate cards (MRP / Distributor / Retailer …) — used to auto-fill rates on new orders"
        actions={
          <>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileRef}
              onChange={uploadFile}
              className="hidden"
              data-testid="price-list-upload-input"
            />
            <Button variant="outline" onClick={downloadTemplate} data-testid="price-list-template-btn">
              <Download className="w-4 h-4" /> Template
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="price-list-upload-btn">
              <Upload className="w-4 h-4" /> Bulk upload
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
              data-testid="new-price-list-btn"
            >
              <Plus className="w-4 h-4" /> New price list
            </Button>
          </>
        }
      />

      {open && (
        <PriceListForm
          products={products}
          initial={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onSave={save}
        />
      )}

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No price lists yet" description="Create your first price list to auto-fill rates while placing orders." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Products</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60" data-testid={`price-list-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 text-[#5C6B5E]">{r.description || "—"}</td>
                    <td className="px-4 py-3">{(r.items || []).length}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold inline-flex items-center gap-1"
                          onClick={() => {
                            setEditing(r);
                            setOpen(true);
                          }}
                          data-testid={`edit-price-list-${r.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          className="text-xs p-1.5 rounded hover:bg-red-50 text-red-600"
                          onClick={() => del(r.id)}
                          data-testid={`delete-price-list-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function PriceListForm({ products, initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [items, setItems] = useState(
    initial?.items?.length ? initial.items.map((i) => ({ ...i })) : [{ product_id: "", rate: 0 }]
  );
  const [busy, setBusy] = useState(false);

  const setItem = (i, patch) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((p) => [...p, { product_id: "", rate: 0 }]);
  const rmItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    const cleaned = items.filter((i) => i.product_id);
    if (!cleaned.length) return toast.error("Add at least one product");
    setBusy(true);
    try {
      await onSave(
        {
          name: name.trim(),
          description,
          items: cleaned.map((i) => ({ product_id: i.product_id, rate: Number(i.rate) || 0 })),
        },
        initial?.id
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="price-list-form">
      <h3 className="font-heading text-xl font-bold mb-4">
        {initial ? "Edit price list" : "New price list"}
      </h3>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Distributor" data-testid="price-list-name" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description" />
          </div>
        </div>

        <div className="space-y-3">
          <Label>Product rates</Label>
          {items.map((it, i) => {
            const prod = products.find((p) => p.id === it.product_id);
            return (
              <div key={`plitem-${i}`} className="grid grid-cols-12 gap-2">
                <div className="col-span-12 sm:col-span-7">
                  <Select
                    value={it.product_id}
                    onChange={(e) => setItem(i, { product_id: e.target.value })}
                    data-testid={`price-list-product-${i}`}
                  >
                    <option value="">Select product…</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.packing_size}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="col-span-8 sm:col-span-4">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Rate"
                      value={it.rate}
                      onChange={(e) => setItem(i, { rate: e.target.value })}
                      data-testid={`price-list-rate-${i}`}
                    />
                    <span className="text-xs text-[#5C6B5E] whitespace-nowrap">
                      /{prod?.unit || "unit"}
                    </span>
                  </div>
                </div>
                <div className="col-span-4 sm:col-span-1 flex items-center">
                  <button type="button" onClick={() => rmItem(i)} className="text-red-600 hover:bg-red-50 rounded p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          <Button variant="outline" type="button" onClick={addItem}>
            <Plus className="w-4 h-4" /> Add product
          </Button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy} data-testid="price-list-submit">
            {busy ? "Saving…" : "Save price list"}
          </Button>
        </div>
        <div className="text-xs text-[#5C6B5E]">
          Tip: leave a product out to fall back to the rate typed manually on the order.
          {" "}Sample MRP amount: {money(items.reduce((a, b) => a + Number(b.rate || 0), 0))} across all rows.
        </div>
      </form>
    </Card>
  );
}
